// src/pages/SetPassword.js
// ──────────────────────────────────────────────────────────────────────────
// First-login password change. Students provisioned with a temporary password
// land here; once they set a new one we clear the must_change_password flag.
// Also usable any time a logged-in user wants to change their password.
// ──────────────────────────────────────────────────────────────────────────

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import theme from '../theme/theme';
import logo from '../assets/star.png';

const Wrap = styled.section`
  max-width: 460px; margin: 0 auto; padding: 4.5rem ${theme.layout.gutter} 6rem; text-align: center;
  img { height: 64px; width: 64px; object-fit: cover; border-radius: 50%; border: 1px solid ${theme.color.line}; margin: 0 auto 1.2rem; display: block; }
  h1 { font-weight: 400; font-size: 1.8rem; } .sub { color: ${theme.color.inkSoft}; margin: 0.5rem 0 1.6rem; }
  label { display: block; text-align: left; font-size: 0.78rem; font-weight: 600; text-transform: uppercase; color: ${theme.color.inkSoft}; margin: 0.9rem 0 0.4rem; }
  input { width: 100%; font-family: ${theme.font.body}; font-size: 1rem; padding: 12px 15px; border: 1px solid ${theme.color.line}; border-radius: ${theme.layout.radius}; }
  input:focus { outline: none; border-color: ${theme.color.accent}; }
  button { margin-top: 1.4rem; width: 100%; background: ${theme.color.accent}; color: ${theme.color.onAccent}; border: none; font-family: ${theme.font.body}; font-weight: 700; font-size: 0.95rem; padding: 13px; border-radius: ${theme.layout.radius}; cursor: pointer; }
  button:disabled { opacity: 0.6; } .err { color: #b3261e; font-size: 0.9rem; margin-top: 1rem; } .ok { color: #1a7f4b; margin-top: 1rem; }
`;

export default function SetPassword() {
  const { user, isStaff, ready } = useAuth();
  const navigate = useNavigate();
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    setErr('');
    if (pw.length < 6) { setErr('Use at least 6 characters.'); return; }
    if (pw !== pw2) { setErr('Passwords don’t match.'); return; }
    setBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: pw });
      if (error) throw error;
      if (user) await supabase.from('profiles').update({ must_change_password: false }).eq('id', user.id);
      navigate(isStaff ? '/portal/staff' : '/portal/student', { replace: true });
    } catch (e) {
      setErr(e?.message || 'Could not update password.');
    } finally { setBusy(false); }
  };

  if (ready && !user) {
    return (<><Navbar /><Wrap><h1>Please sign in first</h1><Link to="/login" style={{ color: theme.color.accent, fontWeight: 700 }}>Go to login</Link></Wrap><Footer /></>);
  }

  return (
    <>
      <Navbar />
      <Wrap>
        <img src={logo} alt="MTI" />
        <h1>Set your password</h1>
        <p className="sub">Choose a new password to finish setting up your account.</p>
        <label>New password</label>
        <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} />
        <label>Confirm password</label>
        <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && submit()} />
        <button onClick={submit} disabled={busy || !pw || !pw2}>{busy ? 'Saving…' : 'Save password'}</button>
        {err && <p className="err">{err}</p>}
      </Wrap>
      <Footer />
    </>
  );
}
